import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { emotionService } from "@/services/emotionService";
import { Settings } from "lucide-react";

interface ApiKeyDialogProps {
  onApiKeySet: () => void;
  initialOpen?: boolean;
}

const API_KEY_STORAGE_KEY = "huggingface_api_key";

const ApiKeyDialog = ({ onApiKeySet, initialOpen = false }: ApiKeyDialogProps) => {
  const [apiKey, setApiKey] = useState<string>(() => {
    const savedKey = localStorage.getItem(API_KEY_STORAGE_KEY) || "";
    if (savedKey) {
      emotionService.setApiKey(savedKey);
    }
    return savedKey;
  });
  
  const [open, setOpen] = useState(initialOpen);

  // Update open state when initialOpen changes
  useEffect(() => {
    if (initialOpen) {
      setOpen(true);
    }
  }, [initialOpen]);

  const handleSaveApiKey = () => {
    if (!apiKey.trim()) {
      toast({
        title: "API Key Required",
        description: "Please enter your Hugging Face API key",
        variant: "destructive",
      });
      return;
    }

    if (!apiKey.startsWith("hf_")) {
      toast({
        title: "Invalid API Key Format",
        description: "Hugging Face API keys typically start with 'hf_'",
        variant: "destructive",
      });
      return;
    }

    // Save to local storage
    localStorage.setItem(API_KEY_STORAGE_KEY, apiKey);
    
    // Set in the service
    emotionService.setApiKey(apiKey);
    
    toast({
      title: "API Key Saved",
      description: "Your Hugging Face API key has been saved",
    });
    
    setOpen(false);
    onApiKeySet();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="icon">
          <Settings className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Hugging Face API Key</DialogTitle>
          <DialogDescription>
            Enter your Hugging Face API key to use the speech emotion recognition model.
            You can get a free API key at{" "}
            <a 
              href="https://huggingface.co/settings/tokens" 
              target="_blank" 
              rel="noreferrer"
              className="text-blue-600 hover:underline"
            >
              https://huggingface.co/settings/tokens
            </a>
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="apiKey" className="text-right">
              API Key
            </Label>
            <Input
              id="apiKey"
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="hf_xxxxxxxxxxxxxxxxxxxx"
              className="col-span-3"
            />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={handleSaveApiKey}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ApiKeyDialog; 